import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Box,
  Flex,
  Grid,
  HStack,
  SimpleGrid,
  Text,
  Badge,
  Button,
  IconButton,
  Alert,
  AlertIcon,
  AlertDescription,
  Spinner,
  Collapse,
  Tooltip,
  useBreakpointValue,
} from '@chakra-ui/react';
import { RefreshCw, Info, Ban } from 'lucide-react';

const ANKS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
const SINGLE_ORDER = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0];

const istTime = (ms) =>
  new Date(ms).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: false });
const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const pattiAnk = (p) => [...String(p)].reduce((s, c) => s + Number(c), 0) % 10;
const isNoDraw = (value) => String(value ?? '').trim().toUpperCase() === 'NA';

const Stat = ({ label, value, color = 'gray.800' }) => (
  <Box bg="white" borderRadius="md" borderWidth="1px" px={3} py={2}>
    <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="700">{label}</Text>
    <Text fontSize="lg" fontWeight="800" color={color}>{value}</Text>
  </Box>
);

/**
 * Tez Motka patti board for one draw, laid out like the player app: pick an ank, see points
 * booked on singles and on that ank's 22 pattis, pick a patti, Continue (or No Draw).
 * Phones get a full-screen layout with the ank picker as a scrollable row.
 */
const MatkaBoardModal = ({ isOpen, onClose, drawId, fetchBoard, onContinue, onNoDraw }) => {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ank, setAnk] = useState(0);
  const [selected, setSelected] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [nowMs, setNowMs] = useState(Date.now());
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;
  const perRow = useBreakpointValue({ base: 4, sm: 6, md: 11 }) ?? 11;

  const load = useCallback(
    async (initial = false) => {
      if (!drawId) return;
      setLoading(true);
      setError('');
      try {
        const res = await fetchBoard(drawId);
        const data = res.data;
        setBoard(data);
        setNowMs(Date.now());
        if (initial) {
          const scheduled = data?.draw?.scheduled?.status === 'pending' ? data.draw.scheduled.result : '';
          const current = String(data?.draw?.result || scheduled || '').split(',')[1];
          if (current) {
            setSelected(current);
            setAnk(pattiAnk(current));
          }
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [drawId, fetchBoard]
  );

  useEffect(() => {
    if (!isOpen) return undefined;
    setBoard(null);
    setAnk(0);
    setSelected(null);
    setShowDetails(false);
    load(true);
    const timer = setInterval(() => setNowMs(Date.now()), 15000);
    return () => clearInterval(timer);
  }, [isOpen, load]);

  const draw = board?.draw;
  const singles = board?.singles || {};
  const pattis = board?.pattis || {};
  const list = useMemo(() => board?.chart?.[ank] || [], [board, ank]);
  const isOpenForResult = draw ? draw.draw_time_epoc <= nowMs : false;
  const scheduled = draw && !draw.result && draw.scheduled?.status === 'pending' ? draw.scheduled : null;
  const declaredNoDraw = draw && isNoDraw(draw.result);
  const scheduledNoDraw = scheduled && isNoDraw(scheduled.result);
  const canNoDraw = Boolean(draw) && !draw.result && !scheduledNoDraw && typeof onNoDraw === 'function';
  const selectedAnk = selected ? pattiAnk(selected) : null;
  const projected = selected
    ? ((Number(singles[selectedAnk]) || 0) + (Number(pattis[selected]) || 0)) * (Number(board?.game?.win) || 0)
    : 0;

  const handleContinue = () => {
    if (selected) onContinue(selected);
  };

  let badge = null;
  if (draw) {
    if (declaredNoDraw) badge = { color: 'red', text: 'No Draw' };
    else if (draw.result) badge = { color: 'green', text: `Declared ${draw.result}` };
    else if (scheduledNoDraw) badge = { color: 'red', text: 'No Draw scheduled' };
    else if (scheduled) badge = { color: 'purple', text: `Scheduled ${scheduled.result}` };
    else badge = isOpenForResult ? { color: 'orange', text: 'Awaiting result' } : { color: 'blue', text: 'Upcoming' };
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={isMobile ? 'full' : '4xl'}
      scrollBehavior="inside"
      isCentered={!isMobile}
    >
      <ModalOverlay backdropFilter="blur(2px)" />
      <ModalContent borderRadius={isMobile ? 0 : '2xl'} overflow="hidden">
        <ModalHeader
          bgGradient="linear(135deg, #000000 0%, #001f3f 60%, #003366 100%)"
          color="white"
          pr={24}
          py={{ base: 3, md: 4 }}
        >
          <Flex gap={2} align="center" wrap="wrap">
            <Text fontSize="lg">Patti Board</Text>
            {badge && (
              <Badge colorScheme={badge.color} borderRadius="full" px={2}>
                {badge.text}
              </Badge>
            )}
          </Flex>
          <Text fontSize="sm" fontWeight="400" opacity={0.8} noOfLines={2}>
            {board?.game ? `${board.game.name} · Game ${board.game.gameNumber}` : ''}
            {draw ? ` · Draw ${draw.draw_id} · ${istTime(draw.draw_time_epoc)}` : ''}
          </Text>
        </ModalHeader>
        <Tooltip label="Refresh" placement="left">
          <IconButton
            aria-label="Refresh"
            icon={<RefreshCw size={16} />}
            size="sm"
            variant="ghost"
            color="white"
            _hover={{ bg: 'whiteAlpha.300' }}
            position="absolute"
            top={3}
            right={12}
            onClick={() => load(false)}
            isLoading={loading && Boolean(board)}
          />
        </Tooltip>
        <ModalCloseButton color="white" top={3} />

        <ModalBody py={{ base: 3, md: 4 }} px={{ base: 3, md: 6 }} bg="gray.100">
          {error && (
            <Alert status="error" borderRadius="md" mb={3}>
              <AlertIcon />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {loading && !board ? (
            <Flex justify="center" py={16}><Spinner size="lg" color="blue.400" /></Flex>
          ) : board ? (
            <Grid templateColumns={{ base: '1fr', md: '88px 1fr' }} gap={3}>
              <Box minW={0}>
                <Text fontSize="xs" fontWeight="800" mb={1}>PATTI</Text>
                <Flex
                  direction={{ base: 'row', md: 'column' }}
                  gap={1.5}
                  overflowX={{ base: 'auto', md: 'visible' }}
                  pb={{ base: 1, md: 0 }}
                >
                  {ANKS.map((a) => (
                    <Button
                      key={a}
                      onClick={() => setAnk(a)}
                      h={{ base: '42px', md: '38px' }}
                      minW={{ base: '42px', md: 'auto' }}
                      flexShrink={0}
                      fontSize="lg"
                      fontWeight="800"
                      color="white"
                      bg={ank === a ? '#0b3d91' : '#1e6fe0'}
                      border="3px solid"
                      borderColor={ank === a ? '#ffd400' : 'transparent'}
                      borderRadius="md"
                      _hover={{ bg: '#0b3d91' }}
                      _active={{ bg: '#082c6a' }}
                    >
                      {a}
                    </Button>
                  ))}
                </Flex>
              </Box>

              <Box minW={0}>
                <Text fontSize="xs" fontWeight="800" mb={1}>SINGLE</Text>
                <SimpleGrid columns={5} spacing={1.5}>
                  {SINGLE_ORDER.map((a) => (
                    <Box
                      key={a}
                      as="button"
                      type="button"
                      onClick={() => setAnk(a)}
                      bg="#e53935"
                      borderRadius="md"
                      border="3px solid"
                      borderColor={ank === a ? '#ffd400' : '#b71c1c'}
                      px={{ base: 1, md: 2 }}
                      pt={1}
                      pb={{ base: 1, md: 2 }}
                      minW={0}
                      _hover={{ filter: 'brightness(1.08)' }}
                    >
                      <Text color="white" fontWeight="800" fontSize={{ base: 'lg', md: 'xl' }} lineHeight="1.2">{a}</Text>
                      <Box bg="white" borderRadius="sm" h={{ base: '20px', md: '24px' }} display="flex" alignItems="center" justifyContent="center">
                        <Text fontSize={{ base: 'xs', md: 'sm' }} fontWeight="700" color={singles[a] ? 'red.600' : 'gray.300'} noOfLines={1}>
                          {singles[a] ? fmt(singles[a]) : '—'}
                        </Text>
                      </Box>
                    </Box>
                  ))}
                </SimpleGrid>

                <Box
                  mt={2}
                  py={1.5}
                  textAlign="center"
                  bg="#b71c1c"
                  color="white"
                  fontWeight="800"
                  borderRadius="md"
                  border="2px solid #ffd400"
                >
                  {list.length} PATTI - {ank}
                </Box>

                <Grid templateColumns={`repeat(${perRow}, minmax(0, 1fr))`} mt={2} borderWidth="1px" borderColor="gray.300">
                  {list.map((p, i) => {
                    const green = (Math.floor(i / perRow) + (i % perRow)) % 2 === 0;
                    const isSel = selected === p;
                    const pts = pattis[p];
                    return (
                      <Box
                        key={p}
                        as="button"
                        type="button"
                        onClick={() => setSelected(isSel ? null : p)}
                        bg={green ? '#5ee39a' : '#f6a8d8'}
                        border="3px solid"
                        borderColor={isSel ? '#ffd400' : 'transparent'}
                        boxShadow={isSel ? '0 0 0 1px #b38f00 inset' : 'none'}
                        minH={{ base: '54px', md: '58px' }}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        _hover={{ filter: 'brightness(1.05)' }}
                      >
                        <Text fontWeight="800" fontSize="md" color="gray.900">{p}</Text>
                        <Text fontSize="xs" fontWeight="800" color="red.600" minH="16px">{pts ? fmt(pts) : ''}</Text>
                      </Box>
                    );
                  })}
                </Grid>

                <Collapse in={showDetails} animateOpacity>
                  <Box mt={3} p={3} bg="gray.50" borderRadius="lg" borderWidth="1px">
                    <SimpleGrid columns={{ base: 2, md: 5 }} spacing={2}>
                      <Stat label="Tickets" value={fmt(board.totals?.tickets)} />
                      <Stat label="Players" value={fmt(board.totals?.players)} />
                      <Stat label="Total points" value={fmt(board.totals?.points)} />
                      <Stat label="Single points" value={fmt(board.totals?.singlePoints)} />
                      <Stat label="Patti points" value={fmt(board.totals?.pattiPoints)} />
                    </SimpleGrid>
                    {selected ? (
                      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2} mt={2}>
                        <Stat label="Selected" value={`${selectedAnk},${selected}`} color="blue.700" />
                        <Stat label={`Points on ank ${selectedAnk}`} value={fmt(singles[selectedAnk])} />
                        <Stat label={`Points on ${selected}`} value={fmt(pattis[selected])} />
                        <Stat label="Projected payout (1x)" value={fmt(projected)} color="red.600" />
                      </SimpleGrid>
                    ) : (
                      <Text mt={2} fontSize="sm" color="gray.500">Select a patti to see its projected payout.</Text>
                    )}
                  </Box>
                </Collapse>
              </Box>
            </Grid>
          ) : null}
        </ModalBody>

        <ModalFooter bg="gray.100" borderTopWidth="1px" px={{ base: 3, md: 6 }} py={{ base: 2, md: 4 }}>
          <Flex w="full" gap={2} wrap="wrap" align="center">
            <Button
              leftIcon={<Info size={16} />}
              bg="#1b8a3a"
              color="white"
              border="2px solid #0f5c25"
              _hover={{ bg: '#167532' }}
              onClick={() => setShowDetails((v) => !v)}
              isDisabled={!board}
              size={{ base: 'sm', md: 'md' }}
            >
              {showDetails ? 'Hide details' : 'View details'}
            </Button>
            <Text fontSize="sm" color="gray.600" mr="auto" flex={{ base: '1 1 auto', md: 'none' }}>
              {selected ? (
                <>Selected <b>{selected}</b> → ank <b>{selectedAnk}</b></>
              ) : (
                'Pick a patti'
              )}
            </Text>
            <HStack spacing={2} w={{ base: 'full', md: 'auto' }}>
              {canNoDraw && (
                <Button
                  leftIcon={<Ban size={16} />}
                  bg="white"
                  color="red.600"
                  border="2px solid"
                  borderColor="red.400"
                  _hover={{ bg: 'red.50' }}
                  onClick={onNoDraw}
                  flex={{ base: 1, md: 'none' }}
                >
                  NO DRAW
                </Button>
              )}
              <Button
                bg="#e91e8c"
                color="white"
                _hover={{ bg: '#c8177a' }}
                onClick={() => setSelected(null)}
                isDisabled={!selected}
                flex={{ base: 1, md: 'none' }}
              >
                CLEAR
              </Button>
              <Tooltip
                label={!isOpenForResult && draw ? `Scheduled now, published automatically at ${istTime(draw.draw_time_epoc)}` : ''}
                isDisabled={isOpenForResult || !draw}
              >
                <Box flex={{ base: 1, md: 'none' }}>
                  <Button
                    w="full"
                    bg="#8b0000"
                    color="#ffd400"
                    border="2px solid #ffd400"
                    _hover={{ bg: '#6f0000' }}
                    onClick={handleContinue}
                    isDisabled={!selected}
                  >
                    CONTINUE
                  </Button>
                </Box>
              </Tooltip>
            </HStack>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MatkaBoardModal;
